import { BACKEND_URL } from "@/utils/apiConfig";
import { Handle, Position } from "reactflow";

const handleStyle = {
  width: "10px",
  height: "10px",
  background: "#fff",
  border: "2px solid #D1D5DB",
  borderRadius: "50%",
};

function BaseNode({ data, children, headerColor }) {
  const imageUrl = data.imageUrl;
  const contentItems = data.contentItems || [];
  const pendingItems = contentItems.filter(c => !c.done);
  const hasPending = pendingItems.length > 0;
  const allDone = contentItems.length > 0 && pendingItems.length === 0;

  return (
    <div className={`bg-white rounded-xl shadow-md flex flex-col overflow-hidden hover:shadow-lg transition-all duration-200 relative ${
      hasPending ? "border-2 border-amber-400 ring-2 ring-amber-200/50" : allDone ? "border-2 border-emerald-300" : "border border-gray-200/80"
    }`} style={{ width: data.nodeWidth || 288 }}>
      {/* Pending content banner */}
      {hasPending && (
        <div className="bg-amber-50 border-b border-amber-200 px-3 py-1.5 flex items-center gap-2" data-testid="pending-content-badge">
          <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse shrink-0" />
          <span className="text-[9px] font-bold text-amber-700">{pendingItems.length} contenu{pendingItems.length > 1 ? "s" : ""} en attente</span>
        </div>
      )}
      {allDone && (
        <div className="bg-emerald-50 border-b border-emerald-200 px-3 py-1.5 flex items-center gap-2" data-testid="content-complete-badge">
          <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 text-emerald-600 shrink-0" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 6l3 3 5-5"/></svg>
          <span className="text-[9px] font-bold text-emerald-700">Contenu complet</span>
        </div>
      )}
      <Handle
        type="target"
        position={Position.Top}
        style={{ ...handleStyle, top: -5 }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left-target"
        style={{ ...handleStyle, left: -5 }}
      />

      {/* Image banner if uploaded */}
      {imageUrl ? (
        <div className="w-full h-16 bg-gray-50 flex items-center justify-center overflow-hidden">
          <img
            src={imageUrl.startsWith("/api") ? `${BACKEND_URL}${imageUrl}` : imageUrl}
            alt={data.title || "Image"}
            className="w-full h-full object-contain p-1"
          />
        </div>
      ) : (
        /* Colored accent bar */
        <div
          className="h-1.5 w-full"
          style={{ backgroundColor: headerColor || data.color || "#6366F1" }}
        />
      )}

      {/* Header */}
      <div className="px-4 pt-3 pb-1 flex items-center gap-2">
        <div
          className="w-2.5 h-2.5 rounded-full shrink-0"
          style={{
            backgroundColor: headerColor || data.color || "#6366F1",
          }}
        />
        <span className="font-semibold text-gray-900 truncate flex-1" style={{ fontSize: (data.fontSize || 13) + 1 }}>
          {data.title}
        </span>
        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider shrink-0">
          {data.nodeLabel || ""}
        </span>
      </div>

      {/* Body */}
      <div className="px-4 pb-3 pt-1 flex flex-col gap-2" style={{ fontSize: data.fontSize || 13 }}>
        {data.calculatedAmount ? (
          <div className="flex items-center gap-1.5">
            <span className="text-lg font-bold font-mono tracking-tight text-violet-600">
              {data.calculatedAmount}
            </span>
            <span className="text-[8px] bg-violet-100 text-violet-600 font-bold px-1.5 py-0.5 rounded-full uppercase">
              fx
            </span>
          </div>
        ) : data.amount ? (
          <div className="text-lg font-bold text-gray-900 font-mono tracking-tight">
            {data.amount}
          </div>
        ) : null}
        {data.description && (
          <p className="text-xs text-gray-500 leading-relaxed">
            {data.description}
          </p>
        )}
        {children}
        {data.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-0.5">
            {data.tags.map((tag, i) => (
              <span
                key={i}
                className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor:
                    (headerColor || data.color || "#6366F1") + "18",
                  color: headerColor || data.color || "#6366F1",
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        {data.customFields?.length > 0 && (
          <div className="border-t border-gray-100 pt-2 mt-1 space-y-1">
            {data.customFields.map((field, i) => (
              <div key={i} className="flex justify-between text-xs">
                <span className="text-gray-400">{field.key}</span>
                <span className="font-semibold text-gray-700">
                  {field.value}
                </span>
              </div>
            ))}
          </div>
        )}
        {data.links?.length > 0 && (
          <div className="border-t border-gray-100 pt-1 mt-1">
            {data.links.map((link, i) => (
              <a
                key={i}
                href={link}
                target="_blank"
                rel="noreferrer"
                className="text-[10px] text-indigo-500 hover:text-indigo-700 underline block truncate"
              >
                {link}
              </a>
            ))}
          </div>
        )}
        {/* Content checklist progress */}
        {contentItems.length > 0 && (
          <div className="border-t border-gray-100 pt-2 mt-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[9px] font-semibold text-gray-400 uppercase">Contenu attendu</span>
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${allDone ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                {contentItems.length - pendingItems.length}/{contentItems.length}
              </span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-1.5">
              <div className="h-full rounded-full transition-all" style={{ width: `${contentItems.length > 0 ? ((contentItems.length - pendingItems.length) / contentItems.length * 100) : 0}%`, backgroundColor: allDone ? "#10B981" : "#F59E0B" }} />
            </div>
            {/* Individual items */}
            <div className="space-y-0.5">
              {contentItems.map((item, i) => (
                <div key={i} className="flex items-center gap-1.5 text-[9px]">
                  {item.done ? (
                    <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 text-emerald-500 shrink-0" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 6l3 3 5-5"/></svg>
                  ) : (
                    <div className="w-2.5 h-2.5 rounded-full border border-amber-400 bg-amber-50 shrink-0" />
                  )}
                  <span className={item.done ? "text-gray-400 line-through" : "text-amber-700 font-semibold"}>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        style={{ ...handleStyle, bottom: -5 }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right-source"
        style={{ ...handleStyle, right: -5 }}
      />
    </div>
  );
}

export function CompanyNode({ data }) {
  return (
    <BaseNode
      data={{ ...data, nodeLabel: "Entreprise" }}
      headerColor={data.color || "#6366F1"}
    />
  );
}

export function SubsidiaryNode({ data }) {
  return (
    <BaseNode
      data={{ ...data, nodeLabel: "Filiale" }}
      headerColor={data.color || "#3B82F6"}
    />
  );
}

export function JobNode({ data }) {
  return (
    <BaseNode
      data={{ ...data, nodeLabel: "Metier" }}
      headerColor={data.color || "#F59E0B"}
    />
  );
}

export function ScenarioNode({ data }) {
  return (
    <BaseNode
      data={{ ...data, nodeLabel: "Scénario" }}
      headerColor={data.color || "#F43F5E"}
    >
      {data.condition && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-2 text-xs">
          <span className="font-semibold text-rose-500 mr-1">SI</span>
          <span className="text-gray-700">{data.condition}</span>
        </div>
      )}
    </BaseNode>
  );
}

/* ---- NEW NODE TYPES ---- */

const miniHandleStyle = {
  width: "8px",
  height: "8px",
  background: "#fff",
  border: "2px solid #D1D5DB",
  borderRadius: "50%",
};

function getInitials(name) {
  if (!name) return "?";
  return name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);
}

export function PersonNode({ data }) {
  const color = data.color || "#7C3AED";
  return (
    <div className="flex flex-col items-center w-36 group">
      <Handle type="target" position={Position.Top} style={{ ...miniHandleStyle, top: -4 }} />
      <Handle type="target" position={Position.Left} id="left-target" style={{ ...miniHandleStyle, left: -4 }} />
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg ring-4 ring-white"
        style={{ background: `linear-gradient(135deg, ${color}, ${color}CC)` }}
      >
        {getInitials(data.title)}
      </div>
      <span className="font-semibold text-sm mt-2 text-center leading-tight text-gray-900">
        {data.title}
      </span>
      {data.description && (
        <span className="text-[10px] text-gray-400 text-center mt-0.5">{data.description}</span>
      )}
      {data.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1 justify-center">
          {data.tags.map((tag, i) => (
            <span key={i} className="text-[9px] font-medium px-1.5 py-0.5 rounded-full" style={{ backgroundColor: color + "18", color }}>
              {tag}
            </span>
          ))}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} style={{ ...miniHandleStyle, bottom: -4 }} />
      <Handle type="source" position={Position.Right} id="right-source" style={{ ...miniHandleStyle, right: -4 }} />
    </div>
  );
}

export function LogoNode({ data }) {
  const color = data.color || "#6366F1";
  const icon = data.icon || data.title?.[0]?.toUpperCase() || "?";
  const imageUrl = data.imageUrl;
  return (
    <div className="flex flex-col items-center w-24 group">
      <Handle type="target" position={Position.Top} style={{ ...miniHandleStyle, top: -4 }} />
      <Handle type="target" position={Position.Left} id="left-target" style={{ ...miniHandleStyle, left: -4 }} />
      {imageUrl ? (
        <div className="w-14 h-14 rounded-2xl overflow-hidden shadow-md ring-2 ring-white">
          <img src={imageUrl.startsWith("/api") ? `${BACKEND_URL}${imageUrl}` : imageUrl} alt={data.title || "Logo"} className="w-full h-full object-cover" />
        </div>
      ) : (
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-md"
          style={{ backgroundColor: color }}
        >
          {icon}
        </div>
      )}
      <span className="text-[11px] font-semibold mt-1.5 text-center text-gray-900 leading-tight">
        {data.title}
      </span>
      {data.amount && (
        <span className="text-[10px] font-mono text-gray-400">{data.amount}</span>
      )}
      <Handle type="source" position={Position.Bottom} style={{ ...miniHandleStyle, bottom: -4 }} />
      <Handle type="source" position={Position.Right} id="right-source" style={{ ...miniHandleStyle, right: -4 }} />
    </div>
  );
}

export function NoteNode({ data }) {
  const colors = {
    "#F59E0B": "bg-amber-50 border-amber-200",
    "#6366F1": "bg-indigo-50 border-indigo-200",
    "#F43F5E": "bg-rose-50 border-rose-200",
    "#3B82F6": "bg-blue-50 border-blue-200",
    "#10B981": "bg-emerald-50 border-emerald-200",
  };
  const cls = colors[data.color] || "bg-amber-50 border-amber-200";
  return (
    <div className={`w-52 ${cls} border rounded-xl p-3.5 shadow-sm rotate-[0.5deg] hover:rotate-0 transition-transform group`}>
      <Handle type="target" position={Position.Top} style={{ ...miniHandleStyle, top: -4 }} />
      <Handle type="target" position={Position.Left} id="left-target" style={{ ...miniHandleStyle, left: -4 }} />
      {data.title && (
        <p className="text-xs font-semibold text-gray-700 mb-1">{data.title}</p>
      )}
      <p className="text-[11px] text-gray-600 leading-relaxed whitespace-pre-wrap">
        {data.description || "Note vide..."}
      </p>
      {data.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {data.tags.map((tag, i) => (
            <span key={i} className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-black/5 text-gray-500">
              {tag}
            </span>
          ))}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} style={{ ...miniHandleStyle, bottom: -4 }} />
      <Handle type="source" position={Position.Right} id="right-source" style={{ ...miniHandleStyle, right: -4 }} />
    </div>
  );
}

export function MetricNode({ data }) {
  const color = data.color || "#6366F1";
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-md w-44 text-center overflow-hidden group">
      <Handle type="target" position={Position.Top} style={{ ...miniHandleStyle, top: -4 }} />
      <Handle type="target" position={Position.Left} id="left-target" style={{ ...miniHandleStyle, left: -4 }} />
      <div className="h-1 w-full" style={{ backgroundColor: color }} />
      <div className="p-4">
        {data.calculatedAmount ? (
          <>
            <span className="text-3xl font-bold font-mono tracking-tighter text-violet-600">
              {data.calculatedAmount}
            </span>
            <span className="text-[8px] bg-violet-100 text-violet-600 font-bold px-1.5 py-0.5 rounded-full uppercase inline-block mt-1">fx</span>
          </>
        ) : (
          <span className="text-3xl font-bold font-mono tracking-tighter" style={{ color }}>
            {data.amount || "0"}
          </span>
        )}
        <span className="text-xs text-gray-500 block mt-1 font-medium">{data.title}</span>
        {data.description && (
          <span className="text-[10px] text-gray-400 block mt-0.5">{data.description}</span>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} style={{ ...miniHandleStyle, bottom: -4 }} />
      <Handle type="source" position={Position.Right} id="right-source" style={{ ...miniHandleStyle, right: -4 }} />
    </div>
  );
}

export function ServiceNode({ data }) {
  const color = data.color || "#6366F1";
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-md w-72 overflow-hidden group">
      <Handle type="target" position={Position.Top} style={{ ...handleStyle, top: -5 }} />
      <Handle type="target" position={Position.Left} id="left-target" style={{ ...handleStyle, left: -5 }} />
      <div className="p-4 flex gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-sm"
          style={{ backgroundColor: color }}
        >
          {data.title?.[0]?.toUpperCase() || "S"}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-sm text-gray-900 truncate">{data.title}</h4>
          {data.description && (
            <p className="text-[11px] text-gray-400 mt-0.5 leading-relaxed line-clamp-2">{data.description}</p>
          )}
          {data.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {data.tags.map((tag, i) => (
                <span key={i} className="text-[9px] font-medium px-1.5 py-0.5 rounded-full" style={{ backgroundColor: color + "15", color }}>
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      {data.customFields?.length > 0 && (
        <div className="border-t border-gray-100 px-4 py-2 space-y-1">
          {data.customFields.map((f, i) => (
            <div key={i} className="flex justify-between text-[11px]">
              <span className="text-gray-400">{f.key}</span>
              <span className="font-medium text-gray-600">{f.value}</span>
            </div>
          ))}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} style={{ ...handleStyle, bottom: -5 }} />
      <Handle type="source" position={Position.Right} id="right-source" style={{ ...handleStyle, right: -5 }} />
    </div>
  );
}

// ─── WEB BLOCK NODES ───
const webBlockStyle = "bg-white rounded-xl shadow-md border-2 border-dashed w-80 flex flex-col overflow-hidden hover:shadow-lg transition-all duration-200";

function WebBlockNode({ data, label, icon, accentColor }) {
  const attachments = data.attachments || [];
  const contentItems = data.contentItems || [];
  const completedItems = contentItems.filter(c => c.done).length;
  const progress = contentItems.length > 0 ? Math.round((completedItems / contentItems.length) * 100) : 0;

  return (
    <div className={webBlockStyle} style={{ borderColor: accentColor + "60" }}>
      <Handle type="target" position={Position.Top} style={{ ...handleStyle, top: -5 }} />
      <Handle type="target" position={Position.Left} id="left-target" style={{ ...handleStyle, left: -5 }} />
      {/* Block type label */}
      <div className="px-4 py-2 flex items-center gap-2" style={{ backgroundColor: accentColor + "10" }}>
        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: accentColor }}>{icon} {label}</span>
        {contentItems.length > 0 && (
          <span className={`ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full ${progress === 100 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
            {completedItems}/{contentItems.length}
          </span>
        )}
      </div>
      <div className="px-4 py-2.5">
        <h4 className="font-semibold text-sm text-gray-900">{data.title}</h4>
        {data.description && <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-2">{data.description}</p>}
        {/* Content progress bar */}
        {contentItems.length > 0 && (
          <div className="mt-2">
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, backgroundColor: progress === 100 ? "#10B981" : accentColor }} />
            </div>
            <div className="mt-1.5 space-y-0.5">
              {contentItems.slice(0, 3).map((item, i) => (
                <div key={i} className="flex items-center gap-1.5 text-[9px]">
                  <div className={`w-3 h-3 rounded border flex items-center justify-center shrink-0 ${item.done ? "bg-emerald-500 border-emerald-500" : "border-gray-300"}`}>
                    {item.done && <svg viewBox="0 0 12 12" className="w-2 h-2 text-white" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 6l3 3 5-5"/></svg>}
                  </div>
                  <span className={item.done ? "text-gray-400 line-through" : "text-gray-600"}>{item.label}</span>
                </div>
              ))}
              {contentItems.length > 3 && <span className="text-[9px] text-gray-400">+{contentItems.length - 3} autres...</span>}
            </div>
          </div>
        )}
        {/* Attachments count */}
        {attachments.length > 0 && (
          <div className="mt-2 flex items-center gap-1 text-[9px] text-gray-400">
            <svg viewBox="0 0 16 16" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 8l-5.5 5.5a3.5 3.5 0 01-5-5L9 3a2.5 2.5 0 013.5 3.5L7 12"/></svg>
            {attachments.length} piece{attachments.length > 1 ? "s" : ""} jointe{attachments.length > 1 ? "s" : ""}
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} style={{ ...handleStyle, bottom: -5 }} />
      <Handle type="source" position={Position.Right} id="right-source" style={{ ...handleStyle, right: -5 }} />
    </div>
  );
}

export function HeaderBlock({ data }) { return <WebBlockNode data={data} label="Header" icon="▬" accentColor="#6366F1" />; }
export function HeroBlock({ data }) { return <WebBlockNode data={data} label="Hero" icon="◆" accentColor="#7C3AED" />; }
export function CtaBlock({ data }) { return <WebBlockNode data={data} label="CTA" icon="▶" accentColor="#F43F5E" />; }
export function SectionBlock({ data }) { return <WebBlockNode data={data} label="Section" icon="▤" accentColor="#3B82F6" />; }
export function FooterBlock({ data }) { return <WebBlockNode data={data} label="Footer" icon="▬" accentColor="#6B7280" />; }
export function FormBlock({ data }) { return <WebBlockNode data={data} label="Formulaire" icon="▥" accentColor="#0EA5E9" />; }


// ─── INFO BLOCK (lists) ───
export function InfoBlock({ data }) {
  const color = data.color || "#6366F1";
  const items = data.listItems || [];
  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-all" style={{ width: data.nodeWidth || 288 }}>
      <Handle type="target" position={Position.Top} style={{ ...handleStyle, top: -5 }} />
      <Handle type="target" position={Position.Left} id="left-target" style={{ ...handleStyle, left: -5 }} />
      <div className="px-3 py-1.5 flex items-center gap-2" style={{ backgroundColor: color + "12" }}>
        <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color }}>INFO</span>
      </div>
      <div className="px-4 py-3" style={{ fontSize: data.fontSize || 13 }}>
        <h4 className="font-semibold text-gray-900" style={{ fontSize: (data.fontSize || 13) + 1 }}>{data.title}</h4>
        {data.description && <p className="text-gray-400 mt-0.5 leading-relaxed" style={{ fontSize: (data.fontSize || 13) - 2 }}>{data.description}</p>}
        {items.length > 0 && (
          <ul className="mt-2 space-y-1">
            {items.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-gray-700" style={{ fontSize: (data.fontSize || 13) - 2 }}>
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} style={{ ...handleStyle, bottom: -5 }} />
      <Handle type="source" position={Position.Right} id="right-source" style={{ ...handleStyle, right: -5 }} />
    </div>
  );
}

// ─── PRICE BLOCK (HT / TTC) ───
export function PriceBlock({ data }) {
  const color = data.color || "#10B981";
  const priceHT = data.priceHT || "0";
  const tvaRate = data.tvaRate || 20;
  const priceTTC = (parseFloat(priceHT) * (1 + tvaRate / 100)).toFixed(2);
  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-all" style={{ width: data.nodeWidth || 240 }}>
      <Handle type="target" position={Position.Top} style={{ ...handleStyle, top: -5 }} />
      <Handle type="target" position={Position.Left} id="left-target" style={{ ...handleStyle, left: -5 }} />
      <div className="px-3 py-1.5 flex items-center gap-2" style={{ backgroundColor: color + "12" }}>
        <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color }}>TARIF</span>
      </div>
      <div className="px-4 py-3" style={{ fontSize: data.fontSize || 13 }}>
        <h4 className="font-semibold text-gray-900" style={{ fontSize: (data.fontSize || 13) + 1 }}>{data.title || "Prestation"}</h4>
        {data.description && <p className="text-gray-400 mt-0.5" style={{ fontSize: (data.fontSize || 13) - 2 }}>{data.description}</p>}
        <div className="mt-3 space-y-1.5 border-t border-gray-100 pt-2">
          <div className="flex justify-between items-baseline">
            <span className="text-gray-400" style={{ fontSize: (data.fontSize || 13) - 2 }}>Prix HT</span>
            <span className="font-bold text-gray-900" style={{ fontSize: (data.fontSize || 13) + 2 }}>{parseFloat(priceHT).toLocaleString("fr-FR")} EUR</span>
          </div>
          <div className="flex justify-between items-baseline">
            <span className="text-gray-400" style={{ fontSize: (data.fontSize || 13) - 2 }}>TVA ({tvaRate}%)</span>
            <span className="text-gray-500" style={{ fontSize: (data.fontSize || 13) - 1 }}>{(parseFloat(priceHT) * tvaRate / 100).toFixed(2)} EUR</span>
          </div>
          <div className="flex justify-between items-baseline pt-1.5 border-t border-gray-200">
            <span className="font-semibold text-gray-700" style={{ fontSize: (data.fontSize || 13) - 1 }}>Total TTC</span>
            <span className="font-bold" style={{ fontSize: (data.fontSize || 13) + 3, color }}>{parseFloat(priceTTC).toLocaleString("fr-FR")} EUR</span>
          </div>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} style={{ ...handleStyle, bottom: -5 }} />
      <Handle type="source" position={Position.Right} id="right-source" style={{ ...handleStyle, right: -5 }} />
    </div>
  );
}


// ─── LABEL NODE (simple title/tag) ───
export function LabelNode({ data }) {
  const color = data.color || "#6366F1";
  const size = data.fontSize || 18;
  return (
    <div className="group" style={{ minWidth: 60 }}>
      <Handle type="target" position={Position.Top} style={{ ...miniHandleStyle, top: -4, opacity: 0 }} />
      <Handle type="target" position={Position.Left} id="left-target" style={{ ...miniHandleStyle, left: -4, opacity: 0 }} />
      <div className="px-1 py-0.5" style={{ fontSize: size, fontWeight: 700, color, lineHeight: 1.2 }}>
        {data.title || "Titre"}
      </div>
      {data.description && (
        <div className="px-1" style={{ fontSize: Math.max(10, size - 5), color: color + "99", lineHeight: 1.3 }}>
          {data.description}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} style={{ ...miniHandleStyle, bottom: -4, opacity: 0 }} />
      <Handle type="source" position={Position.Right} id="right-source" style={{ ...miniHandleStyle, right: -4, opacity: 0 }} />
    </div>
  );
}

// ─── LEGEND NODE (color legend) ───
export function LegendNode({ data }) {
  const items = data.legendItems || [];
  const fs = data.fontSize || 13;
  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-gray-200 shadow-sm overflow-hidden" style={{ width: data.nodeWidth || 200 }}>
      <Handle type="target" position={Position.Top} style={{ ...miniHandleStyle, top: -4, opacity: 0 }} />
      <Handle type="target" position={Position.Left} id="left-target" style={{ ...miniHandleStyle, left: -4, opacity: 0 }} />
      <div className="px-3 py-2 border-b border-gray-100">
        <span className="font-bold text-gray-500 uppercase tracking-wider" style={{ fontSize: fs - 2 }}>{data.title || "Legende"}</span>
      </div>
      <div className="px-3 py-2" style={{ gap: fs * 0.4, display: "flex", flexDirection: "column" }}>
        {items.length > 0 ? items.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="rounded-sm shrink-0" style={{ backgroundColor: item.color || "#6366F1", width: fs, height: fs }} />
            <span className="text-gray-700" style={{ fontSize: fs }}>{item.label || "..."}</span>
          </div>
        )) : (
          <p className="text-gray-400 italic" style={{ fontSize: fs - 3 }}>Ajoutez des elements via le panneau</p>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} style={{ ...miniHandleStyle, bottom: -4, opacity: 0 }} />
      <Handle type="source" position={Position.Right} id="right-source" style={{ ...miniHandleStyle, right: -4, opacity: 0 }} />
    </div>
  );
}

